import { memo } from 'react';
import './Castle.css';

/**
 * Castle visualization — Omani-Islamic architectural style.
 * Six unique fortresses inspired by Nizwa, Bahla, Jabreen and Jalali forts:
 * round drum towers, pointed arches, crescent finials, domes, and geometric
 * pattern bands. Fills with a "rising water" animation. Cracks softly when
 * the reader misses a day.
 */

// Crescent (هلال) finial — replaces the cross-like flag of generic castles.
const Crescent = ({ cx, cy, scale = 1, color }) => (
  <g className="castle-crescent">
    <line x1={cx} y1={cy} x2={cx} y2={cy - 8 * scale} stroke={color} strokeWidth={1.2 * scale} strokeLinecap="round" />
    <path
      d={`M ${cx - 3.5 * scale} ${cy - 8 * scale}
          a ${3.5 * scale} ${3.5 * scale} 0 1 1 ${7 * scale} 0
          a ${2.4 * scale} ${2.4 * scale} 0 1 0 -${5.5 * scale} 0 Z`}
      fill={color}
      stroke="rgba(0,0,0,0.15)"
      strokeWidth={0.4}
    />
  </g>
);

// Pointed Arabic arch (قوس مدبب)
const ArchPath = (x, y, w, h) => {
  const peakY = y - h * 0.3;
  return `M ${x} ${y} L ${x} ${y - h * 0.55} Q ${x} ${peakY} ${x + w / 2} ${peakY} Q ${x + w} ${peakY} ${x + w} ${y - h * 0.55} L ${x + w} ${y} Z`;
};

// Crenellation (شرفة) — rounded notches typical of Omani forts
const Crenellation = (startX, y, count, step, notchH = 5) => {
  let d = `M ${startX} ${y}`;
  for (let i = 0; i < count; i++) {
    const x = startX + i * step;
    d += ` L ${x + step * 0.3} ${y} L ${x + step * 0.3} ${y - notchH} L ${x + step * 0.7} ${y - notchH} L ${x + step * 0.7} ${y}`;
  }
  d += ` L ${startX + count * step} ${y}`;
  return d;
};

// Geometric Islamic pattern band — interlaced diamonds
const PatternBand = ({ x, y, width, color, id }) => (
  <g style={{ opacity: 0.65 }}>
    <pattern id={id} x="0" y="0" width="14" height="6" patternUnits="userSpaceOnUse">
      <path d="M 7 0 L 14 3 L 7 6 L 0 3 Z" fill="none" stroke={color} strokeWidth="0.6" />
      <circle cx="7" cy="3" r="0.8" fill={color} />
    </pattern>
    <rect x={x} y={y} width={width} height="6" fill={`url(#${id})`} />
  </g>
);

const CASTLE_DESIGNS = [
  // 0 — البداية : a single humble drum tower
  {
    viewBox: '0 0 140 160',
    render: (color, patId) => (
      <g>
        {/* Ground */}
        <rect x="20" y="148" width="100" height="8" fill={color} fillOpacity="0.25" rx="2" />
        {/* Body */}
        <rect x="48" y="60" width="44" height="92" rx="4" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" />
        {/* Crenellation top */}
        <path d={Crenellation(48, 60, 5, 8.8, 5)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.4" />
        {/* Dome */}
        <path d="M 48 60 Q 70 38 92 60 Z" fill={color} fillOpacity="0.35" stroke={color} strokeWidth="1.4" />
        <Crescent cx={70} cy={38} scale={0.9} color={color} />
        {/* Pointed arched window */}
        <path d={ArchPath(60, 130, 20, 28)} fill="rgba(0,0,0,0.18)" stroke={color} strokeWidth="0.8" />
        {/* Pattern */}
        <PatternBand x={48} y={108} width={44} color={color} id={`${patId}-0`} />
      </g>
    ),
  },
  // 1 — البرونزي : two side towers + central arched gate
  {
    viewBox: '0 0 200 170',
    render: (color, patId) => (
      <g>
        <rect x="14" y="158" width="172" height="8" fill={color} fillOpacity="0.25" rx="2" />
        {/* Wall body */}
        <rect x="32" y="92" width="136" height="68" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" />
        <path d={Crenellation(32, 92, 12, 11.3, 6)} fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.3" />
        {/* Round towers */}
        <rect x="14" y="60" width="32" height="100" rx="6" fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.5" />
        <path d="M 14 60 Q 30 38 46 60 Z" fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1.3" />
        <Crescent cx={30} cy={38} scale={0.9} color={color} />
        <rect x="154" y="60" width="32" height="100" rx="6" fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.5" />
        <path d="M 154 60 Q 170 38 186 60 Z" fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1.3" />
        <Crescent cx={170} cy={38} scale={0.9} color={color} />
        {/* Central arched gate */}
        <path d={ArchPath(82, 160, 36, 60)} fill="rgba(0,0,0,0.22)" stroke={color} strokeWidth="0.8" />
        <PatternBand x={32} y={130} width={136} color={color} id={`${patId}-1`} />
      </g>
    ),
  },
  // 2 — الفضي : three towers with central dome
  {
    viewBox: '0 0 240 180',
    render: (color, patId) => (
      <g>
        <rect x="14" y="168" width="212" height="8" fill={color} fillOpacity="0.25" rx="2" />
        {/* Outer wall */}
        <rect x="22" y="100" width="196" height="68" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.5" />
        <path d={Crenellation(22, 100, 16, 12.25, 6)} fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.3" />
        {/* Side drum towers */}
        <rect x="6" y="68" width="32" height="100" rx="6" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.5" />
        <path d="M 6 68 Q 22 46 38 68 Z" fill={color} fillOpacity="0.42" stroke={color} strokeWidth="1.3" />
        <Crescent cx={22} cy={46} scale={0.9} color={color} />
        <rect x="202" y="68" width="32" height="100" rx="6" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.5" />
        <path d="M 202 68 Q 218 46 234 68 Z" fill={color} fillOpacity="0.42" stroke={color} strokeWidth="1.3" />
        <Crescent cx={218} cy={46} scale={0.9} color={color} />
        {/* Central tower with onion dome */}
        <rect x="100" y="42" width="40" height="126" rx="5" fill={color} fillOpacity="0.28" stroke={color} strokeWidth="1.5" />
        <path d="M 100 42 Q 100 22 120 14 Q 140 22 140 42 Z" fill={color} fillOpacity="0.5" stroke={color} strokeWidth="1.4" />
        <Crescent cx={120} cy={14} scale={1.1} color={color} />
        {/* Arched gate */}
        <path d={ArchPath(108, 168, 24, 50)} fill="rgba(0,0,0,0.22)" stroke={color} strokeWidth="0.8" />
        {/* Arched windows on side towers */}
        <path d={ArchPath(14, 145, 16, 22)} fill="rgba(0,0,0,0.18)" stroke={color} strokeWidth="0.6" />
        <path d={ArchPath(210, 145, 16, 22)} fill="rgba(0,0,0,0.18)" stroke={color} strokeWidth="0.6" />
        <PatternBand x={22} y={138} width={196} color={color} id={`${patId}-2`} />
      </g>
    ),
  },
  // 3 — الذهبي : grand central keep with onion dome + four towers
  {
    viewBox: '0 0 260 200',
    render: (color, patId) => (
      <g>
        <rect x="10" y="188" width="240" height="8" fill={color} fillOpacity="0.25" rx="2" />
        {/* Outer fortress wall */}
        <rect x="22" y="120" width="216" height="68" fill={color} fillOpacity="0.16" stroke={color} strokeWidth="1.5" />
        <path d={Crenellation(22, 120, 18, 12, 6)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.2" />
        {/* Inner wall higher */}
        <rect x="60" y="84" width="140" height="36" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.3" />
        <path d={Crenellation(60, 84, 12, 11.7, 5)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.2" />
        {/* Corner drum towers */}
        {[0, 1].map(i => (
          <g key={i}>
            <rect x={i ? 220 : 8} y="86" width="28" height="102" rx="5" fill={color} fillOpacity="0.25" stroke={color} strokeWidth="1.4" />
            <path d={`M ${i ? 220 : 8} 86 Q ${i ? 234 : 22} 66 ${i ? 248 : 36} 86 Z`} fill={color} fillOpacity="0.42" stroke={color} strokeWidth="1.2" />
            <Crescent cx={i ? 234 : 22} cy={66} scale={0.85} color={color} />
          </g>
        ))}
        {/* Central tall tower with onion dome */}
        <rect x="112" y="46" width="40" height="142" rx="4" fill={color} fillOpacity="0.32" stroke={color} strokeWidth="1.6" />
        <path d="M 112 46 Q 110 22 132 10 Q 154 22 152 46 Z" fill={color} fillOpacity="0.55" stroke={color} strokeWidth="1.5" />
        <Crescent cx={132} cy={10} scale={1.2} color={color} />
        {/* Side small minarets */}
        {[0, 1].map(i => (
          <g key={`m${i}`}>
            <rect x={i ? 178 : 70} y="100" width="14" height="88" fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.1" />
            <path d={`M ${i ? 178 : 70} 100 Q ${i ? 185 : 77} 86 ${i ? 192 : 84} 100 Z`} fill={color} fillOpacity="0.38" stroke={color} strokeWidth="1" />
            <Crescent cx={i ? 185 : 77} cy={86} scale={0.6} color={color} />
          </g>
        ))}
        {/* Pointed arch gate */}
        <path d={ArchPath(118, 188, 28, 56)} fill="rgba(0,0,0,0.24)" stroke={color} strokeWidth="0.8" />
        {/* Arched windows */}
        <path d={ArchPath(120, 92, 24, 28)} fill="rgba(0,0,0,0.2)" stroke={color} strokeWidth="0.6" />
        <PatternBand x={22} y={158} width={216} color={color} id={`${patId}-3`} />
      </g>
    ),
  },
  // 4 — البلاتيني : citadel with five domes + tiered walls
  {
    viewBox: '0 0 280 220',
    render: (color, patId) => (
      <g>
        <rect x="8" y="206" width="264" height="10" fill={color} fillOpacity="0.25" rx="2" />
        {/* Lower wall */}
        <rect x="16" y="146" width="248" height="60" fill={color} fillOpacity="0.16" stroke={color} strokeWidth="1.5" />
        <path d={Crenellation(16, 146, 22, 11.27, 6)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.2" />
        {/* Mid wall */}
        <rect x="50" y="106" width="180" height="40" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.4" />
        <path d={Crenellation(50, 106, 16, 11.25, 5)} fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.2" />
        {/* Five-dome cluster on top */}
        {[0, 1, 2, 3, 4].map(i => {
          const cx = 64 + i * 38;
          return (
            <g key={i}>
              <rect x={cx - 14} y="68" width="28" height="40" fill={color} fillOpacity={0.24 + i * 0.02} stroke={color} strokeWidth="1.2" />
              <path d={`M ${cx - 14} 68 Q ${cx - 14} 50 ${cx} 42 Q ${cx + 14} 50 ${cx + 14} 68 Z`} fill={color} fillOpacity={0.4 + i * 0.04} stroke={color} strokeWidth="1.2" />
              <Crescent cx={cx} cy={42} scale={i === 2 ? 1.1 : 0.7} color={color} />
            </g>
          );
        })}
        {/* Corner round towers */}
        {[0, 1].map(i => (
          <g key={`c${i}`}>
            <rect x={i ? 244 : 4} y="112" width="28" height="94" rx="5" fill={color} fillOpacity="0.26" stroke={color} strokeWidth="1.4" />
            <path d={`M ${i ? 244 : 4} 112 Q ${i ? 258 : 18} 94 ${i ? 272 : 32} 112 Z`} fill={color} fillOpacity="0.42" stroke={color} strokeWidth="1.2" />
            <Crescent cx={i ? 258 : 18} cy={94} scale={0.85} color={color} />
          </g>
        ))}
        {/* Grand arched gate */}
        <path d={ArchPath(124, 206, 32, 60)} fill="rgba(0,0,0,0.26)" stroke={color} strokeWidth="0.9" />
        <PatternBand x={50} y={132} width={180} color={color} id={`${patId}-4a`} />
        <PatternBand x={16} y={184} width={248} color={color} id={`${patId}-4b`} />
      </g>
    ),
  },
  // 5 — الماسي : majestic citadel — central minaret, dual minarets, multiple domes
  {
    viewBox: '0 0 320 240',
    render: (color, patId) => (
      <g>
        <rect x="6" y="226" width="308" height="10" fill={color} fillOpacity="0.25" rx="2" />
        {/* Ramparts */}
        <rect x="14" y="166" width="292" height="62" fill={color} fillOpacity="0.16" stroke={color} strokeWidth="1.5" />
        <path d={Crenellation(14, 166, 24, 12.17, 6)} fill={color} fillOpacity="0.2" stroke={color} strokeWidth="1.2" />
        {/* Inner palace wall */}
        <rect x="46" y="124" width="228" height="42" fill={color} fillOpacity="0.18" stroke={color} strokeWidth="1.4" />
        <path d={Crenellation(46, 124, 19, 12, 5)} fill={color} fillOpacity="0.22" stroke={color} strokeWidth="1.2" />
        {/* Side mini-domes */}
        {[0, 1, 2, 3].map(i => {
          const cx = 70 + i * 60;
          return (
            <g key={i}>
              <rect x={cx - 14} y="86" width="28" height="38" fill={color} fillOpacity="0.24" stroke={color} strokeWidth="1.2" />
              <path d={`M ${cx - 14} 86 Q ${cx - 14} 70 ${cx} 62 Q ${cx + 14} 70 ${cx + 14} 86 Z`} fill={color} fillOpacity="0.4" stroke={color} strokeWidth="1.2" />
              <Crescent cx={cx} cy={62} scale={0.7} color={color} />
            </g>
          );
        })}
        {/* Two flanking minarets */}
        {[0, 1].map(i => (
          <g key={`min${i}`}>
            <rect x={i ? 210 : 92} y="76" width="18" height="50" fill={color} fillOpacity="0.26" stroke={color} strokeWidth="1.2" />
            <path d={`M ${i ? 210 : 92} 76 Q ${i ? 219 : 101} 60 ${i ? 228 : 110} 76 Z`} fill={color} fillOpacity="0.42" stroke={color} strokeWidth="1.1" />
            <Crescent cx={i ? 219 : 101} cy={60} scale={0.9} color={color} />
          </g>
        ))}
        {/* Central grand keep with onion dome */}
        <rect x="138" y="40" width="44" height="186" rx="4" fill={color} fillOpacity="0.34" stroke={color} strokeWidth="1.7" />
        <path d="M 138 40 Q 134 14 160 4 Q 186 14 182 40 Z" fill={color} fillOpacity="0.6" stroke={color} strokeWidth="1.6" />
        <Crescent cx={160} cy={4} scale={1.4} color={color} />
        {/* Corner drum towers */}
        {[0, 1].map(i => (
          <g key={`dt${i}`}>
            <rect x={i ? 282 : 4} y="132" width="34" height="96" rx="6" fill={color} fillOpacity="0.28" stroke={color} strokeWidth="1.5" />
            <path d={`M ${i ? 282 : 4} 132 Q ${i ? 299 : 21} 112 ${i ? 316 : 38} 132 Z`} fill={color} fillOpacity="0.46" stroke={color} strokeWidth="1.3" />
            <Crescent cx={i ? 299 : 21} cy={112} scale={1} color={color} />
          </g>
        ))}
        {/* Triple-arch gate */}
        <path d={ArchPath(146, 226, 28, 60)} fill="rgba(0,0,0,0.28)" stroke={color} strokeWidth="0.9" />
        <path d={ArchPath(146, 100, 28, 26)} fill="rgba(0,0,0,0.2)" stroke={color} strokeWidth="0.7" />
        <PatternBand x={46} y={150} width={228} color={color} id={`${patId}-5a`} />
        <PatternBand x={14} y={204} width={292} color={color} id={`${patId}-5b`} />
      </g>
    ),
  },
];

const CastleVisual = ({
  index = 0,
  fillPercent = 0,
  cracked = false,
  color = '#C19A6B',
  size = 'md',
  levelName = '',
}) => {
  const idx = Math.max(0, Math.min(CASTLE_DESIGNS.length - 1, index));
  const design = CASTLE_DESIGNS[idx];
  const fill = Math.max(0, Math.min(100, fillPercent));
  const dim =
    size === 'xl' ? 'w-48 h-48'
    : size === 'lg' ? 'w-36 h-36'
    : size === 'sm' ? 'w-8 h-8'
    : 'w-24 h-24';
  const idSuffix = `${idx}-${size}-${Math.random().toString(36).slice(2, 7)}`;

  return (
    <div className={`castle-visual ${dim} ${cracked ? 'castle-cracked' : ''}`} data-testid="castle-visual">
      <svg viewBox={design.viewBox} preserveAspectRatio="xMidYMax meet" className="w-full h-full">
        <defs>
          <linearGradient id={`fill-${idSuffix}`} x1="0" x2="0" y1="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity="0.95" />
            <stop offset="100%" stopColor={color} stopOpacity="0.45" />
          </linearGradient>
          <filter id={`glow-${idSuffix}`} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Faint silhouette layer */}
        <g style={{ opacity: 0.95 }}>
          {design.render(color, idSuffix)}
        </g>

        {/* Rising fill overlay — clipped to a rect that grows from the bottom */}
        <g style={{ mixBlendMode: 'multiply' }}>
          <clipPath id={`clip-${idSuffix}`}>
            <rect
              x="0"
              y={`${100 - fill}%`}
              width="100%"
              height={`${fill}%`}
              className="castle-fill-rect"
            />
          </clipPath>
          <g clipPath={`url(#clip-${idSuffix})`}>
            <rect x="0" y="0" width="100%" height="100%" fill={`url(#fill-${idSuffix})`} />
          </g>
        </g>

        {/* Wave shimmer at fill surface */}
        {fill > 0 && fill < 100 && (
          <line
            x1="0"
            x2="100%"
            y1={`${100 - fill}%`}
            y2={`${100 - fill}%`}
            stroke="white"
            strokeOpacity="0.5"
            strokeWidth="1.5"
            className="castle-fill-wave"
          />
        )}

        {/* Cracks — soft, hand-drawn looking */}
        {cracked && (
          <g className="castle-crack-group" style={{ pointerEvents: 'none' }}>
            <path
              d="M 80 70 Q 84 80 78 92 Q 86 106 80 122 Q 88 138 82 152"
              stroke="rgba(0,0,0,0.4)"
              strokeWidth="1.2"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="3 3"
            />
          </g>
        )}
      </svg>
      {levelName && size !== 'sm' && (
        <p className="castle-name" style={{ color }}>{levelName}</p>
      )}
    </div>
  );
};

export default memo(CastleVisual);
